import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import { UserKyc } from "../types/auth";
import { DealRoom } from "../types/deaalroom";
import { fetchPropertiesSuccess, updatePropertyApprovalStatus } from "./propertySlice";
//import User from "../types/auth"

/* ================= TYPES ================= */

export interface Admin {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  image_url?: string;
}

export interface User {
  id: number;
  uuid: string;
  role_id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  bvn: string;
  nin: string;
  gender: string;
  phone: string;
  address: string;
  state: string;
  country: string;
  approved: boolean;
  // status: string;
  status: 'active' | 'inactive';
  image?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
  email_verified: number;

  kyc?: UserKyc | null;
  //kyc_status?: "pending" | "approved" | "rejected";

}

interface AdminState {
  admin: Admin | null;
  access_token: string | null;
  loading: boolean;
  error: any;
  success: string | null;
  justLoggedIn: boolean;

  users: User[];
  usersLoading: boolean;
  usersError: any;

  dealRooms: DealRoom[];
  dealRoom: DealRoom | null;
  lawyers: User[];

  dealRoomsLoading: boolean;
  dealRoomsError: any;

  profile: Admin | null;
  profileLoading: boolean;
}

/* ================= INITIAL ================= */

const storedToken = localStorage.getItem("admin_token");
const storedAdmin = localStorage.getItem("admin");

const initialState: AdminState = {
  admin: storedAdmin ? JSON.parse(storedAdmin) : null,
  access_token: storedToken,
  loading: false,
  error: null,
  success: null,
  justLoggedIn: false,


  users: [],
  usersLoading: false,
  usersError: null,

  dealRooms: [],
  dealRoom: null,
  lawyers: [],

  dealRoomsLoading: false,
  dealRoomsError: null,

  profile: storedAdmin ? JSON.parse(storedAdmin) : null,
  profileLoading: false,
};

/* ================= API HELPER ================= */

const getAuthToken = (state: RootState, role: "user" | "admin") => {
  return role === "admin"
    ? state.admin.access_token
    : state.auth.access_token;
};

const getAdminToken = (getState: () => RootState) => {
  return getState().admin.access_token || localStorage.getItem("admin_token");
};

async function api<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  token?: string,
  body?: any
): Promise<T> {
  let res: Response;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Server error. Unable to reach the server.");
  }

  let data: any = null;

  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const validationMessage = Object.values(data?.errors || {})
      .flat()
      .find(Boolean);

    const message =
      data?.message ||
      data?.error ||
      validationMessage ||
      (res.status === 401
        ? "Invalid credentials"
        : res.status >= 500
        ? "Server error. Please try again later."
        : "Something went wrong. Please try again.");

    throw new Error(String(message));
  }

  return data as T;
}

/* ================= SLICE ================= */

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
      state.success = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error =
        action.payload?.message || action.payload || "Something went wrong";
    },

    loginSuccess(
      state,
      action: PayloadAction<{ admin: Admin; access_token: string }>
    ) {
      state.loading = false;
      state.admin = action.payload.admin;
      state.access_token = action.payload.access_token;
      state.success = "Login successful";
      state.error = null;
      state.justLoggedIn = true; // ✅ IMPORTANT

      localStorage.setItem("admin_token", action.payload.access_token);
      localStorage.setItem("admin", JSON.stringify(action.payload.admin));
    },

    clearAdminFeedback(state) {
      state.error = null;
      state.success = null;
      state.justLoggedIn = false;
    },

    logout(state) {
      state.admin = null;
      state.access_token = null;
      state.loading = false;

      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin");

      localStorage.removeItem("access_token");
      localStorage.removeItem("persist:root");
      localStorage.clear();
    },

    /* USERS */
    fetchUsersStart(state) {
      state.usersLoading = true;
    },
    fetchUsersSuccess(state, action: PayloadAction<User[]>) {
      state.usersLoading = false;
      state.users = action.payload;
    },
    fetchUsersFail(state, action: PayloadAction<any>) {
      state.usersLoading = false;
      state.usersError = action.payload;
    },

    activateUserSuccess(state, action: PayloadAction<number>) {
      const userId = action.payload;

      const index = state.users.findIndex(u => u.id === userId);
      if (index !== -1) {
        state.users[index].status = "active";
        state.users[index].approved = true;
      }
    },

    deactivateUserSuccess(state, action: PayloadAction<number>) {
      const userId = action.payload;

      const index = state.users.findIndex(u => u.id === userId);
      if (index !== -1) {
        state.users[index].status = "inactive";
        state.users[index].approved = false;
      }
    },

    /*  CHANGE ROLE */
    changeRoleSuccess(state, action: PayloadAction<User>) {
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) state.users[index] = action.payload;
    },

    updateKycStatusSuccess(
      state,
      action: PayloadAction<{ userId: number; status: string }>
    ) {
      const user = state.users.find(u => u.id === action.payload.userId);

      if (user?.kyc) {
        user.kyc.kyc_status = action.payload.status as any;
      }
    },

    /* DEAL ROOMS */

    setDealRooms(state, action: PayloadAction<DealRoom[]>) {
      state.dealRooms = action.payload;
      state.dealRoomsLoading = false;
    },

    setDealRoom(state, action: PayloadAction<DealRoom>) {
      state.dealRoom = action.payload;
      state.dealRoomsLoading = false;
    },

    setLawyers(state, action: PayloadAction<User[]>) {
      state.lawyers = action.payload;
    },

    setAdminProfile(state, action: PayloadAction<Admin>) {
      state.profile = action.payload;
      state.profileLoading = false;

      if (state.admin) {
        state.admin = { ...state.admin, ...action.payload };
        localStorage.setItem("admin", JSON.stringify(state.admin));
      }
    },

    setProfileLoading(state, action: PayloadAction<boolean>) {
      state.profileLoading = action.payload;
    },

    setSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
    },
  },
});

export const {
  start,
  fail,
  loginSuccess,
  logout,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  activateUserSuccess,
  deactivateUserSuccess,
  changeRoleSuccess,

  setDealRooms,
  setDealRoom,
  setLawyers,
  setAdminProfile,
  setProfileLoading,
  setSuccess,

  updateKycStatusSuccess,
  clearAdminFeedback
} = adminSlice.actions;

export default adminSlice.reducer;

/* ================= THUNKS ================= */

/**
 * ADMIN LOGIN
 */
export const adminLogin =
  (body: { email: string; password: string }) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const data = await api<{
          access_token: string;
          admin: Admin;
        }>("/admin/login", "POST", undefined, body);

        dispatch(
          loginSuccess({
            admin: data.admin,
            access_token: data.access_token,
          })
        );

        return data;
      } catch (err: any) {
        dispatch(fail(err.message)); // ✅ only message
      }
    };


/**
* FETCH USERS
*/
export const fetchUsers =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(fetchUsersStart());

      try {
        //const token = getState().admin.access_token;
        const token = getAuthToken(getState(), "admin");

        const res = await api<{
          success: boolean;
          data: User[];
        }>("/admin/users", "GET", token!);

        dispatch(fetchUsersSuccess(res.data));
      } catch (err: any) {
        dispatch(fetchUsersFail(err));
      }
    };


export const adminFetchProperties =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAdminToken(getState);

        if (!token) {
          throw new Error("Unauthorized. Please log in as admin.");
        }

        const response = await api<{
          success: boolean;
          message?: string;
          data: any[] | { data?: any[] };
        }>("/admin/properties", "GET", token);

        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];

        const properties = raw.map((property: any) => {
          let images = property.images;

          if (typeof images === "string") {
            try {
              images = JSON.parse(images);
            } catch {
              images = [];
            }
          }

          return {
            ...property,
            images: Array.isArray(images) ? images : [],
            average_rating:
              Number(
                property.comments_avg_rating ??
                property.average_rating ??
                0
              ) || 0,
          };
        });

        dispatch(fetchPropertiesSuccess(properties));
        dispatch(setSuccess("Properties fetched successfully."));

        return properties;
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to fetch properties.";

        dispatch(fail(message));
        throw err;
      }
    };

/**
* APPROVE PROPERTY
*/

export const approveProperty =
  (id: number | string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const property = getState().property.properties.find(
        (item) => String(item.id) === String(id)
      );

      const previousStatus = property?.admin_approval_status ?? "pending";

      const token = getAuthToken(getState(), "admin");

      if (!token) {
        throw new Error("Unauthorized. Please log in as admin.");
      }

      try {
        const response = await api<{
          success: boolean;
          message: string;
          data: any;
        }>(`/admin/properties/${id}/approve`, "POST", token);

        if (!response.success) {
          throw new Error(response.message || "Failed to approve property.");
        }

        // Use the status returned by the backend.
        dispatch(
          updatePropertyApprovalStatus({
            id,
            status: response.data.admin_approval_status ?? "approved",
          })
        );

        dispatch(
          setSuccess(response.message || "Property approved successfully.")
        );

        void dispatch(adminFetchProperties()).catch((error) => {
          console.error("Failed to refresh properties:", error);
        });

        return response;
      } catch (error) {
        dispatch(
          updatePropertyApprovalStatus({
            id,
            status: previousStatus,
          })
        );

        const message =
          error instanceof Error ? error.message : "Failed to approve property.";

        dispatch(fail(message));
        throw error;
      }
    };

/**
 * REJECT PROPERTY
 */

export const rejectProperty =
  (id: number | string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const property = getState().property.properties.find(
        (item) => String(item.id) === String(id)
      );

      const previousStatus = property?.admin_approval_status ?? "pending";

      const token = getAuthToken(getState(), "admin");
      if (!token) {
        throw new Error("Unauthorized. Please log in as admin.");
      }

      try {
        const response = await api<{
          success: boolean;
          message: string;
          data: any;
        }>(`/admin/properties/${id}/reject`, "POST", token);

        if (!response.success) {
          throw new Error(response.message || "Failed to reject property.");
        }

        dispatch(
          updatePropertyApprovalStatus({
            id,
            status: response.data.admin_approval_status ?? "rejected",
          })
        );

        dispatch(
          setSuccess(response.message || "Property rejected successfully.")
        );

        void dispatch(adminFetchProperties()).catch((error) => {
          console.error("Failed to refresh properties:", error);
        });

        return response;
      } catch (error) {
        dispatch(
          updatePropertyApprovalStatus({
            id,
            status: previousStatus,
          })
        );

        const message =
          error instanceof Error ? error.message : "Failed to reject property.";

        dispatch(fail(message));
        throw error;
      }
    };

/**
 * ACTIVATE USER
 */

export const activateUser =
  (id: number) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getState().admin.access_token;

        await api(
          `/admin/users/${id}/activate`,
          "POST",
          token!
        );

        // ✅ only send ID
        dispatch(activateUserSuccess(id));
      } catch (err: any) {
        dispatch(fail(err.message));
        throw err;
      }
    };



/**
 * DEACTIVATE USER
 */


export const deactivateUser =
  (id: number) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getState().admin.access_token;

        await api(
          `/admin/users/${id}/deactivate`,
          "POST",
          token!
        );

        dispatch(deactivateUserSuccess(id));
      } catch (err: any) {
        dispatch(fail(err.message));
        throw err;
      }
    };

export const updateKycStatusnnn =
  (userId: number, status: "approved" | "rejected" | "pending") =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getState().admin.access_token;

        await api(
          `/admin/kyc/${userId}/status`,
          "POST",
          token!,
          { status }
        );

        dispatch(
          updateKycStatusSuccess({
            userId,
            status,
          })
        );
      } catch (err: any) {
        dispatch(fail(err.message));
        throw err;
      }
    };

export const updateKycStatus =
  (userId: number, status: "approved" | "rejected" | "pending") =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getState().admin.access_token;



        const res = await api(
          `/admin/users/kyc/${userId}/status`,
          "POST",
          token!,
          { status }
        );

        //console.log("KYC RESPONSE:", res);

        dispatch(updateKycStatusSuccess({ userId, status }));
      } catch (err: any) {

        dispatch(fail(err.message));
        throw err;
      }
    };

/**
 *  CHANGE USER ROLE
 */
export const changeUserRole =
  (id: number, role_id: number) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getState().admin.access_token;

        const res = await api<{
          success: boolean;
          message: string;
          user: User;
        }>(
          `/admin/users/${id}/change-role`,
          "PUT",
          token!,
          { role_id }
        );

        dispatch(changeRoleSuccess(res.user));
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

// DEALROOM
export const fetchAdminDealRooms =
  () =>
    async (
      dispatch: AppDispatch,
      getState: () => RootState
    ) => {

      dispatch(start());

      try {

        const token = getAdminToken(getState);

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: DealRoom[];
          message: string;
        }>(
          "/admin/deal-room",
          "GET",
          token
        );

        dispatch(setDealRooms(data.data));
        dispatch(setSuccess(data.message));

        return data;

      } catch (err: any) {

        dispatch(fail(err.message));
        throw err;
      }
    };

export const fetchAdminDealRoom =
  (id: number | string) =>
    async (
      dispatch: AppDispatch,
      getState: () => RootState
    ) => {

      dispatch(start());

      try {

        const token = getAdminToken(getState);

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: DealRoom;
          message: string;
        }>(
          `/admin/deal-room/${id}`,
          "GET",
          token
        );

        dispatch(setDealRoom(data.data));

        return data;

      } catch (err: any) {

        dispatch(fail(err.message));
        throw err;
      }
    };

export const fetchLawyers =
  () =>
    async (
      dispatch: AppDispatch,
      getState: () => RootState
    ) => {

      dispatch(start());

      try {

        const token = getAdminToken(getState);

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: User[];
        }>(
          "/admin/lawyers",
          "GET",
          token
        );

        dispatch(setLawyers(data.data));

        return data;

      } catch (err: any) {

        dispatch(fail(err.message));
        throw err;
      }
    };

/* ================= PROFILE & SECURITY ================= */

export const fetchAdminProfile =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(setProfileLoading(true));

      try {
        const token = getAdminToken(getState);

        if (!token) {
          throw new Error("Unauthorized. Please log in as admin.");
        }

        const response = await api<any>("/admin/profile", "GET", token);

        const profile =
          response?.data?.admin ??
          response?.data ??
          response?.admin ??
          response;

        if (!profile || typeof profile !== "object") {
          throw new Error("Unable to load the admin profile.");
        }

        dispatch(setAdminProfile(profile as Admin));

        return profile as Admin;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to load the admin profile.";

        dispatch(fail(message));
        throw error;
      } finally {
        dispatch(setProfileLoading(false));
      }
    };

export const changeAdminPassword =
  (body: {
    old_password: string;
    new_password: string;
    new_password_confirmation: string;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const token = getAdminToken(getState);

        if (!token) {
          throw new Error("Unauthorized. Please log in as admin.");
        }

        const response = await api<{
          success?: boolean;
          message?: string;
        }>("/admin/change-password", "POST", token, body);

        dispatch(setSuccess(response.message || "Password updated."));

        return response;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to change the password.";

        dispatch(fail(message));
        throw error;
      }
    };

export type { AdminState };